import React, { useRef } from "react"
import PropTypes from "prop-types"
import { NavLink, useMatch, Link } from "react-router-dom"
import { routes } from "router/routes"
import UserMenu from "components/common/UserMenu"
import GuideNavigation from "./GuideNavigation"
import TripFiltersNavigation from "./TripFiltersNavigation"
import SearchTripsAntd from "./SearchTripsAntd"
import withDefaultErrorBoundary from "modules/errors/HOCs/withDefaultErrorBoundary"
import Logo from "components/common/Logo"
import useEvent from "hooks/useEvent"
import { useDispatch, useSelector } from "react-redux"
import { updateWindowSize } from "modules/window/reducer"
import { isGuideSelector, isUserSelector, updateUser } from "store/user"
import { useTheme } from "context/AntdThemeContext"
import { themes } from "constants/theme"
import { useTranslation } from "react-i18next"
import { isProApp } from "utils/app-type"
import { getHostsByEnvironment } from "utils/host-urls"
import { Button, Flex, Switch, Layout, Typography, Space } from "antd"
import styles from "./style.module.css"

const { Header: AntdHeader, Content } = Layout
const { Title } = Typography

GuestHeader.propTypes = {
  noPadding: PropTypes.bool,
  fluid: PropTypes.bool,
  withNavigation: PropTypes.bool,
  withBotomBorder: PropTypes.bool,
  className: PropTypes.string
}

function GuestHeader({ noPadding = false, fluid = false, withNavigation = false, withBotomBorder: bottomBorder = false, className }) {
  const dispatch = useDispatch()
  const headerRef = useRef(null)
  const headerHeight = useSelector((store) => store.window)
  const { switchTheme, isSportsman } = useTheme()
  const isGuide = useSelector(isGuideSelector)
  const isUser = useSelector(isUserSelector)
  const { loggedIn } = useSelector((store) => store.auth)
  const { t } = useTranslation()

  const isProHost = isProApp()
  const { PROTOCOL, PRO_LANDING } = getHostsByEnvironment()
  const isClientLandingPage = !!useMatch(routes.rootPath()) && !isProHost
  const isGuideProfile = !!useMatch(routes.proGuideProfilePath())

  const isTripsPage = !!useMatch(routes.tripsPath())
  const rootPath = (isGuide && routes.guideRootPath()) || (isUser && routes.clientRootPath()) || routes.rootPath()

  const showGuideNavigation = withNavigation && isGuide
  const showTripFiltersNavigation = (isTripsPage && !isGuide) || isClientLandingPage
  const withBotomBorder = bottomBorder || showGuideNavigation || showTripFiltersNavigation

  const setHeight = () => {
    if (!headerRef.current) return
    const height = headerRef.current.clientHeight
    const isHeightChanged = headerHeight !== height
    if (!isHeightChanged) return
    document.body.style.setProperty("--header-height", `${height}px`)
    dispatch(updateWindowSize({ headerHeight: height }))
  }

  useEvent("load", setHeight, true, window, true, true)
  useEvent("resize", setHeight, true, window)

  const classes = [styles.headerRoot, "sticky-top"]
  if (withBotomBorder) classes.push(styles.withBotomBorder)
  if (className) classes.push(className)

  const handleThemeChange = (checked) => {
    switchTheme(isSportsman ? themes.sportswoman : themes.sportsman)
    dispatch(updateUser({ is_sportsman: !isSportsman }))
  }

  const scrollToRequestDemo = () => {
    const demoRequestEl = document.querySelector("#demo-request")
    if (demoRequestEl) demoRequestEl.scrollIntoView({ behavior: "smooth" })
  }

  return (
    <div
      className={styles.stickyTopHeader}
      ref={headerRef}
      style={{ ...(isTripsPage && { maxHeight: 140 }), position: "sticky", top: 0, zIndex: 1000 }}
    >
      <AntdHeader className={`${classes.join(" ")}, layout-wrapper`} style={{ background: "#fff" }}>
        <Content style={{ width: "100%" }}>
          <Flex gap={10} justify="space-between" wrap="wrap" align="center">
            <Space direction="horizontal" size={32} align="center">
              <NavLink
                to={rootPath}
                state={{ scrollToTop: true }}
                title="Home"
                style={{ display: "block", borderRadius: 4, padding: 2, margin: -2 }}
                className={styles.logoWrapper}
              >
                {isSportsman ? (
                  <Logo alt="Logo" width={35} height={35} wide />
                ) : (
                  <Logo alt="Logo" width={35} height={35} is_sportswoman wide />
                )}
              </NavLink>
              {!loggedIn && (
                <>
                  {!isProHost && (
                    <a href={PROTOCOL + PRO_LANDING} target="_blank" rel="noopener noreferrer">
                      <Button className={styles.headerBtn} type="primary">
                        {t("global.become_a_guide")}
                      </Button>
                    </a>
                  )}
                  {isProHost && !isGuideProfile && (
                    <Button className={styles.headerBtn} type="link" style={{ fontFamily: "Epilogue" }} onClick={scrollToRequestDemo}>
                      See a Demo
                    </Button>
                  )}
                </>
              )}
            </Space>
            <div>{!isProHost && <SearchTripsAntd className={styles.searchTrips} />}</div>
            {!loggedIn && (
              <Link to={routes.signInV2()}>
                <Button className={`${styles.headerBtn} px-m py-s`} type="primary">
                  {t("global.sign_in")}
                </Button>
              </Link>
            )}
            {!loggedIn && <UserMenu className="d-block d-md-none" />}
            {loggedIn && (
              <div className="my-2">
                <Flex align="center" gap={10}>
                  <Switch checked={isSportsman} onChange={handleThemeChange} className={styles.themeToggle} />
                  <UserMenu />
                </Flex>
              </div>
            )}
          </Flex>
        </Content>
      </AntdHeader>
      {showGuideNavigation ? <GuideNavigation /> : null}
      {showTripFiltersNavigation ? <TripFiltersNavigation visible={isClientLandingPage} fluid={!isClientLandingPage} /> : null}
    </div>
  )
}

export default withDefaultErrorBoundary(GuestHeader)
